import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {push} from 'connected-react-router';

import {USERS_SERVICE_URL} from 'urls';

import {
  getJsonFromLocalStorage,
  removeFromLocalStorage,
  storeLocally,
} from 'utils/localStorage';

import request from 'utils/request';

import {
  FETCH_READERS,
  FETCH_USERS_FOR_SPECIFIC_ROLE,
  fetchReadersFailed,
  fetchReadersSuccess,
  fetchUsersForSpecificRoleFailed,
  fetchUsersForSpecificRoleSuccess,
  FORGOT_PASSWORD,
  forgotPasswordFailed,
  forgotPasswordSuccess,
  initialLoadUserDone,
  LOAD_USER_FROM_LOCAL_STORAGE,
  RESET_PASSWORD,
  resetPasswordFailed,
  resetPasswordSuccess,
  SEARCH_USERS,
  searchUsersFailed,
  searchUsersSuccess,
  SIGNIN,
  SIGNIN_SUCCESS,
  signinFailed,
  signinSuccess,
  SIGNOUT,
  SIGNUP,
  SIGNUP_SUCCESS,
  signupFailed,
  signupSuccess,
  UPDATE_USER,
  UPDATE_USER_ROLE,
  updateUserFailed,
  updateUserRoleFailed,
  updateUserRoleSuccess,
  updateUserSuccess,
} from 'actions/userActions';

import {makeSelectUserToken} from 'reducers/selectors/userSelectors';
import {getQueryString} from 'utils/queryString';

function* signout() {
  removeFromLocalStorage('user');
  yield put(push('/login'));
}

function* loadUserFromLocalStorage() {
  const localUser = getJsonFromLocalStorage('user');
  if (localUser) localUser.authToken = localUser.token; // TODO if user too long, more then 4K, it failed, changed to use localStorage.  rename all related code to use token,not authToken.
  yield put(initialLoadUserDone(localUser));
}

// Signin Actions
function* signin(action) {
  const requestUrl = `${USERS_SERVICE_URL}login`;
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(action.credentials),
  };
  try {
    const user = yield call(request, requestUrl, undefined, requestOptions);
    storeLocally('user', user); // TODO if user too long, more then 4K, it failed, changed to use localStorage.
    yield put(signinSuccess(user));
  } catch (err) {
    let errorMessage = 'Your login details are incorrect.';
    if (err.message) {
      errorMessage = err.message;
    }

    yield put(signinFailed(errorMessage));
  }
}

// Signup Actions
function* signup(action) {
  const requestUrl = `user/register`;
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(action.userDetails),
  };
  try {
    const user = yield call(request, requestUrl, undefined, requestOptions);
    yield put(signupSuccess(user.user));
  } catch (err) {
    yield put(signupFailed(err.message));
  }
}

function* forgotPassword(action) {
  const requestUrl = `user/forgetPassword`;
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({email: action.email}),
  };
  try {
    const user = yield call(request, requestUrl, undefined, requestOptions);
    yield put(forgotPasswordSuccess(user));
  } catch (err) {
    yield put(forgotPasswordFailed(err));
  }
}

function* resetPassword(action) {
  const requestUrl = `user/resetPassword`;
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(action.details),
  };
  try {
    const user = yield call(request, requestUrl, undefined, requestOptions);
    yield put(resetPasswordSuccess(user));
  } catch (err) {
    yield put(resetPasswordFailed(err.message));
  }
}

function* updateUser(action) {
  const requestUrl = `user/${action.id || action.details._id}`;
  const authToken = yield select(makeSelectUserToken());

  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(action.details),
  };
  try {
    const user = yield call(request, requestUrl, authToken, requestOptions);
    yield put(updateUserSuccess(user.user));
  } catch (err) {
    const error = err.message;
    yield put(
      updateUserFailed(error, action.id || action.details._id || 'new_user')
    );
  }
}

function* updateUserRole(action) {
  const requestUrl = `user/${action.id}/modifyRole`;
  const authToken = yield select(makeSelectUserToken());

  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify({masterRoleId: action.masterRoleId}),
  };
  try {
    const user = yield call(request, requestUrl, authToken, requestOptions);
    yield put(updateUserRoleSuccess(user.user));
  } catch (err) {
    yield put(updateUserRoleFailed(err));
  }
}

function* searchUsers(action) {
  const url = `user/filterByParams?role=${action.role}&searchKey=${action.searchKey}&pageSize=${action.pageSize}&pageNumber=${action.pageNumber}`;
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, url, authToken, {});
    yield put(searchUsersSuccess(response.masterRoles));
  } catch (err) {
    yield put(searchUsersFailed(err));
  }
}

function* fetchUsersForSpecificRole(action) {
  // const { pageSize = 50, pageNumber = 0, role } = action.filters;
  // const url = `user/filterByParams?pageSize=${pageSize}&pageNumber=${pageNumber}&${role}=true`;
  const {role} = action;
  let url = `user/filterByParams?${role}=true`;
  if (action.filters) {
    Object.entries(action.filters).forEach(([key, value]) => {
      url += `&${key}=${value}`;
    });
  }
  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, url, authToken, {});
    const {users, total, pageNumberBack, statuses} = response;
    yield put(
      fetchUsersForSpecificRoleSuccess({
        users,
        total,
        pageNumberBack,
        statuses,
      })
    );
  } catch (err) {
    yield put(fetchUsersForSpecificRoleFailed(err));
  }
}

function* fetchReaders(action) {
  const params = getQueryString(action.filters);
  const url = `reader/readers?${params}`;

  const authToken = yield select(makeSelectUserToken());
  try {
    const response = yield call(request, url, authToken, {});
    yield put(fetchReadersSuccess(response.user));
  } catch (err) {
    yield put(fetchReadersFailed(err));
  }
}

export default function* userSaga() {
  yield all([
    takeLatest(LOAD_USER_FROM_LOCAL_STORAGE, loadUserFromLocalStorage),
    takeLatest(SIGNOUT, signout),
    takeEvery(SIGNIN, signin),
    takeLatest(SIGNIN_SUCCESS, signinSuccess),
    takeLatest(SIGNUP, signup),
    takeLatest(SIGNUP_SUCCESS, signupSuccess),
    takeLatest(FORGOT_PASSWORD, forgotPassword),
    takeLatest(RESET_PASSWORD, resetPassword),
    takeLatest(UPDATE_USER, updateUser),
    takeLatest(SEARCH_USERS, searchUsers),
    takeLatest(UPDATE_USER_ROLE, updateUserRole),
    takeLatest(FETCH_READERS, fetchReaders),
    takeLatest(FETCH_USERS_FOR_SPECIFIC_ROLE, fetchUsersForSpecificRole),
  ]);
}
